export type PoliticalMatchStoryEconomicValue = 'menos_liberdade_economica' | 'centro' | 'mais_liberdade_economica';
export type PoliticalMatchStoryPersonalValue = 'menos_liberdade_pessoal' | 'centro' | 'mais_liberdade_pessoal';

export type PoliticalMatchStoryMatch = {
  nome: string;
  liberdadeEconomica: PoliticalMatchStoryEconomicValue;
  liberdadePessoal: PoliticalMatchStoryPersonalValue;
  posicaoTabela: {
    linha: PoliticalMatchStoryPersonalValue;
    coluna: PoliticalMatchStoryEconomicValue;
  };
  lead: string[];
};

const politicalMatchArauto = {
  nome: 'O Arauto',
  liberdadeEconomica: 'menos_liberdade_economica',
  liberdadePessoal: 'mais_liberdade_pessoal',
  posicaoTabela: { linha: 'mais_liberdade_pessoal', coluna: 'menos_liberdade_economica' },
  lead: [
    'Você enxerga a política como um campo de **mudança**. Quando algo exclui ou limita demais, sua resposta é abrir espaço e reduzir barreiras.',
    'Seu match aponta uma visão que prefere **abrir caminhos**, reduzir barreiras e fazer a liberdade caber na vida de mais gente.',
  ],
} satisfies PoliticalMatchStoryMatch;

const politicalMatchAndarilho = {
  nome: 'O Andarilho',
  liberdadeEconomica: 'centro',
  liberdadePessoal: 'mais_liberdade_pessoal',
  posicaoTabela: { linha: 'mais_liberdade_pessoal', coluna: 'centro' },
  lead: [
    'Você gosta de caminhar fora das rotas prontas. Valoriza **liberdade de escolha** e desconfia de respostas políticas rígidas.',
    'Seu match conversa com um jeito de pensar que valoriza **liberdade de escolha**, leveza e a chance de viver sem moldes fechados.',
  ],
} satisfies PoliticalMatchStoryMatch;

const politicalMatchDesbravador = {
  nome: 'O Desbravador',
  liberdadeEconomica: 'mais_liberdade_economica',
  liberdadePessoal: 'mais_liberdade_pessoal',
  posicaoTabela: { linha: 'mais_liberdade_pessoal', coluna: 'mais_liberdade_economica' },
  lead: [
    'Você aposta no espaço individual para criar, escolher e avançar. **Autonomia** e iniciativa pesam mais do que tutela.',
    'Seu match revela uma visão de **autonomia** e coragem para decidir, criar e avançar sem esperar autorização de fora.',
  ],
} satisfies PoliticalMatchStoryMatch;

const politicalMatchForjador = {
  nome: 'O Forjador',
  liberdadeEconomica: 'menos_liberdade_economica',
  liberdadePessoal: 'centro',
  posicaoTabela: { linha: 'centro', coluna: 'menos_liberdade_economica' },
  lead: [
    'Você vê a política como construção coletiva. **Instituições** e ação pública importam quando ajudam a derrubar barreiras reais.',
    'Seu match aponta uma visão que confia em **instituições** fortes para organizar a vida comum e abrir oportunidades onde elas faltam.',
  ],
} satisfies PoliticalMatchStoryMatch;

const politicalMatchConselheiro = {
  nome: 'O Conselheiro',
  liberdadeEconomica: 'centro',
  liberdadePessoal: 'centro',
  posicaoTabela: { linha: 'centro', coluna: 'centro' },
  lead: [
    'Você evita os extremos e lê cada tema com calma. Para você, a boa política nasce de **equilíbrio** entre liberdade, responsabilidade e estabilidade.',
    'Seu match reflete um perfil que busca **equilíbrio**, mede consequências e prefere caminhos que sustentem o todo.',
  ],
} satisfies PoliticalMatchStoryMatch;

const politicalMatchEstrategista = {
  nome: 'O Estrategista',
  liberdadeEconomica: 'mais_liberdade_economica',
  liberdadePessoal: 'centro',
  posicaoTabela: { linha: 'centro', coluna: 'mais_liberdade_economica' },
  lead: [
    'Você olha menos para rótulos e mais para o que entrega **resultados**. Incentivos, consequências e viabilidade contam muito.',
    'Seu match revela uma cabeça **estratégica**, mais voltada ao que sai do papel, se sustenta e faz diferença de verdade.',
  ],
} satisfies PoliticalMatchStoryMatch;

const politicalMatchBastiao = {
  nome: 'O Bastião',
  liberdadeEconomica: 'menos_liberdade_economica',
  liberdadePessoal: 'menos_liberdade_pessoal',
  posicaoTabela: { linha: 'menos_liberdade_pessoal', coluna: 'menos_liberdade_economica' },
  lead: [
    'Você valoriza estruturas fortes para atravessar crises sem desmontar o conjunto. **Ordem** e coordenação são centrais para você.',
    'Seu match conversa com uma visão que vê **ordem** e limites claros como base para proteger a estabilidade coletiva.',
  ],
} satisfies PoliticalMatchStoryMatch;

const politicalMatchSentinela = {
  nome: 'O Sentinela',
  liberdadeEconomica: 'centro',
  liberdadePessoal: 'menos_liberdade_pessoal',
  posicaoTabela: { linha: 'menos_liberdade_pessoal', coluna: 'centro' },
  lead: [
    'Você observa mudanças com atenção e não acha que tudo o que é antigo deva ser descartado. Há valor em preservar **instituições** e referências.',
    'Seu match aponta um jeito de pensar que valoriza **preservar** o que importa e avançar sem apagar memória, referência e continuidade.',
  ],
} satisfies PoliticalMatchStoryMatch;

const politicalMatchGuardiao = {
  nome: 'O Guardião',
  liberdadeEconomica: 'mais_liberdade_economica',
  liberdadePessoal: 'menos_liberdade_pessoal',
  posicaoTabela: { linha: 'menos_liberdade_pessoal', coluna: 'mais_liberdade_economica' },
  lead: [
    'Você combina iniciativa com responsabilidade. Acredita em autonomia econômica, mas também em princípios que dão continuidade à vida social.',
    'Seu match revela uma visão que quer avançar com **tradição**, responsabilidade e senso de continuidade, sem romper o que sustenta a comunidade.',
  ],
} satisfies PoliticalMatchStoryMatch;

export const politicalMatchStoryMatches: PoliticalMatchStoryMatch[] = [
  politicalMatchArauto,
  politicalMatchAndarilho,
  politicalMatchDesbravador,
  politicalMatchForjador,
  politicalMatchConselheiro,
  politicalMatchEstrategista,
  politicalMatchBastiao,
  politicalMatchSentinela,
  politicalMatchGuardiao,
];

export const politicalMatchStoryTable: Record<
  PoliticalMatchStoryPersonalValue,
  Record<PoliticalMatchStoryEconomicValue, PoliticalMatchStoryMatch>
> = {
  mais_liberdade_pessoal: {
    menos_liberdade_economica: politicalMatchArauto,
    centro: politicalMatchAndarilho,
    mais_liberdade_economica: politicalMatchDesbravador,
  },
  centro: {
    menos_liberdade_economica: politicalMatchForjador,
    centro: politicalMatchConselheiro,
    mais_liberdade_economica: politicalMatchEstrategista,
  },
  menos_liberdade_pessoal: {
    menos_liberdade_economica: politicalMatchBastiao,
    centro: politicalMatchSentinela,
    mais_liberdade_economica: politicalMatchGuardiao,
  },
};
